import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { tw } from "twind";
import { RowHeightContext } from "hooks/useRowMaxHeight";
import { useElementSize } from "hooks/useElementSize";
import { Portal } from "composants/Portal";

interface HtmlCellRendererProps {
  value: string;
  colId: string;
  rowIndex: number;
  /** Ouvre l'aperçu au double-clic uniquement quand la cellule n'est pas éditable. */
  editable?: boolean;
  title?: string;
}

/** Rectangle de la cellule au moment du double-clic (viewport). */
interface PreviewAnchor {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

interface HtmlPreview {
  html: string;
  title?: string;
  anchor: PreviewAnchor;
}

const HtmlPreviewContext = createContext<
  (html: string, anchor: PreviewAnchor, title?: string) => void
>(() => {});

const VIEWPORT_MARGIN = 8;
const CELL_GAP = 4;

function measureHtmlPreview(html: string, title?: string) {
  const maxWidth = window.innerWidth - VIEWPORT_MARGIN * 2;
  const maxHeight = window.innerHeight - VIEWPORT_MARGIN * 2;

  const probe = document.createElement("div");
  probe.style.position = "fixed";
  probe.style.left = "-10000px";
  probe.style.top = "0";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  probe.style.display = "flex";
  probe.style.flexDirection = "column";
  probe.style.boxSizing = "border-box";
  probe.style.width = "max-content";
  probe.style.maxWidth = "none";
  probe.style.border = "1px solid transparent";

  const header = document.createElement("header");
  header.className = "modal-card-head";
  header.style.padding = "6px 10px";
  header.style.width = "max-content";
  header.style.boxSizing = "border-box";

  const titleEl = document.createElement("div");
  titleEl.className = "modal-card-title";
  titleEl.style.fontSize = "0.95rem";
  titleEl.style.lineHeight = "1.3";
  titleEl.style.flexShrink = "0";
  titleEl.textContent = title ?? "";

  const close = document.createElement("button");
  close.className = "delete";
  close.type = "button";
  header.append(titleEl, close);

  const body = document.createElement("section");
  body.className = `modal-card-body ${tw("text-sm")} html-cell-preview`;
  body.style.width = "max-content";
  body.style.maxWidth = "none";
  body.style.height = "auto";
  body.style.overflow = "visible";
  body.style.boxSizing = "border-box";
  body.innerHTML = html ?? "";

  probe.append(header, body);
  document.body.appendChild(probe);

  body.querySelectorAll("img, video").forEach((node) => {
    if (node instanceof HTMLElement) {
      node.style.maxWidth = "none";
    }
  });

  let width = Math.max(probe.scrollWidth, header.scrollWidth, body.scrollWidth);
  body.querySelectorAll("img").forEach((node) => {
    if (node instanceof HTMLImageElement && node.naturalWidth > 0) {
      const chrome = body.offsetWidth - body.clientWidth;
      width = Math.max(width, node.naturalWidth + chrome);
    }
  });
  width = Math.min(Math.ceil(width), maxWidth);

  probe.style.width = `${width}px`;
  probe.style.maxWidth = `${width}px`;
  header.style.width = "auto";
  titleEl.style.flexShrink = "1";
  titleEl.style.minWidth = "0";
  body.style.width = "auto";
  body.style.maxWidth = "100%";
  body.querySelectorAll("img, video").forEach((node) => {
    if (node instanceof HTMLElement) {
      node.style.maxWidth = "100%";
      node.style.height = "auto";
    }
  });

  const height = Math.min(Math.ceil(probe.offsetHeight) + 2, maxHeight);
  document.body.removeChild(probe);

  return { width, height };
}

function computeOverlayPosition(anchor: PreviewAnchor, size: { width: number; height: number }) {
  const maxLeft = window.innerWidth - VIEWPORT_MARGIN - size.width;
  const maxTop = window.innerHeight - VIEWPORT_MARGIN - size.height;

  let left = anchor.left;
  if (left > maxLeft) {
    left = anchor.right - size.width;
  }
  left = Math.min(Math.max(left, VIEWPORT_MARGIN), Math.max(VIEWPORT_MARGIN, maxLeft));

  // Au-dessus de la cellule. Si ça sort de l'écran, on bascule en dessous, puis on recale.
  let top = anchor.top - size.height - CELL_GAP;
  if (top < VIEWPORT_MARGIN) {
    const below = anchor.bottom + CELL_GAP;
    if (below + size.height <= window.innerHeight - VIEWPORT_MARGIN) {
      top = below;
    } else {
      top = Math.min(Math.max(VIEWPORT_MARGIN, top), Math.max(VIEWPORT_MARGIN, maxTop));
    }
  }

  return { left, top };
}

function navigateFromAnchor(anchor: HTMLAnchorElement) {
  let href = anchor.href;
  const origin = `${window.location.protocol}//${window.location.host}`;

  // si c'est un navigation locale, on bloque le behavior par défaut
  // puis on substring le host pour ne push que le pathname à react-router.
  if (href.includes(origin)) {
    href = href.substring(origin.length);
    return { href, local: true };
  }
  return { href, local: false };
}

function HtmlPreviewOverlay({
  html,
  title,
  anchor,
  onClose,
}: {
  html: string;
  title?: string;
  anchor: PreviewAnchor;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  const place = useCallback(() => {
    const size = measureHtmlPreview(html, title);
    const next = { ...computeOverlayPosition(anchor, size), ...size };
    setBox((prev) => {
      if (
        prev &&
        prev.width === next.width &&
        prev.height === next.height &&
        Math.abs(prev.left - next.left) < 0.5 &&
        Math.abs(prev.top - next.top) < 0.5
      ) {
        return prev;
      }
      return next;
    });
  }, [anchor, html, title]);

  useLayoutEffect(() => {
    place();
  }, [place]);

  useEffect(() => {
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [place]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    const onPointerDown = (event: MouseEvent) => {
      const card = cardRef.current;
      if (!card || !(event.target instanceof Node) || card.contains(event.target)) {
        return;
      }
      onClose();
    };
    const openedAt = Date.now();
    const onScroll = (event: Event) => {
      if (Date.now() - openedAt < 150) {
        return;
      }
      const card = cardRef.current;
      if (card && event.target instanceof Node && card.contains(event.target)) {
        return;
      }
      onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [onClose]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) {
      return;
    }
    const images = Array.from(card.querySelectorAll("img"));
    if (images.some((img) => img.complete && img.naturalWidth > 0)) {
      place();
    }
    const pending = images.filter((img) => !img.complete);
    pending.forEach((img) => img.addEventListener("load", place));
    return () => pending.forEach((img) => img.removeEventListener("load", place));
  }, [html, place]);

  return (
    <Portal>
      <div
        ref={cardRef}
        role="dialog"
        aria-label={title}
        style={{
          position: "fixed",
          zIndex: 40,
          left: box?.left ?? 0,
          top: box?.top ?? 0,
          width: box?.width,
          height: box?.height,
          visibility: box ? "visible" : "hidden",
          pointerEvents: box ? "auto" : "none",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
          maxHeight: `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`,
          margin: 0,
          overflow: "hidden",
          backgroundColor: "var(--system-surface, #fff)",
          color: "var(--system-text, #363636)",
          border: "1px solid var(--system-border-2, #dbdbdb)",
          borderRadius: "var(--system-radius-lg, 6px)",
          boxShadow: "0 12px 40px var(--system-shadow, rgba(0, 0, 0, 0.18))",
        }}
      >
        <header className="modal-card-head" style={{ padding: "6px 10px" }}>
          <div
            className="modal-card-title"
            style={{ flexShrink: 1, minWidth: 0, fontSize: "0.95rem", lineHeight: 1.3 }}
          >
            {title}
          </div>
          <button type="button" className="delete" aria-label="close" onClick={onClose} />
        </header>
        <section
          className={`modal-card-body ${tw("text-sm")} html-cell-preview`}
          style={{
            width: "auto",
            maxWidth: "100%",
            minWidth: 0,
            height: "auto",
            minHeight: 0,
            flex: "1 1 auto",
            overflow: "auto",
            boxSizing: "border-box",
          }}
          dangerouslySetInnerHTML={{ __html: html ?? "" }}
          onClick={(event) => {
            const target = event.target;
            if (!(target instanceof HTMLAnchorElement)) {
              return;
            }
            const { href, local } = navigateFromAnchor(target);
            if (local) {
              event.preventDefault();
            }
            navigate(href);
            onClose();
          }}
        />
        <style>{`
          .html-cell-preview img,
          .html-cell-preview video {
            max-width: 100%;
            height: auto;
          }
        `}</style>
      </div>
    </Portal>
  );
}

/** L'overlay vit hors de la cellule : AG Grid recycle les renderers au scroll. */
export function HtmlPreviewProvider({ children }: { children: ReactNode }) {
  const [preview, setPreview] = useState<HtmlPreview | null>(null);
  const open = useCallback((html: string, anchor: PreviewAnchor, title?: string) => {
    setPreview({ html, anchor, title });
  }, []);
  const close = useCallback(() => setPreview(null), []);

  return (
    <HtmlPreviewContext.Provider value={open}>
      {children}
      {preview && (
        <HtmlPreviewOverlay
          html={preview.html}
          title={preview.title}
          anchor={preview.anchor}
          onClose={close}
        />
      )}
    </HtmlPreviewContext.Provider>
  );
}

export default function HtmlCellRenderer({
  value,
  colId,
  rowIndex,
  editable = false,
  title,
}: HtmlCellRendererProps) {
  const registerHeight = useContext(RowHeightContext);
  const openPreview = useContext(HtmlPreviewContext);
  const { ref: sizeRef, height: contentHeight } = useElementSize<HTMLDivElement>();
  const divRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      divRef.current = el;
      sizeRef.current = el;
    },
    [sizeRef],
  );

  useEffect(() => {
    if (registerHeight && contentHeight > 0) {
      registerHeight(rowIndex, contentHeight + 7); // 7px pour le padding top et bottom
    }
  }, [contentHeight, rowIndex, registerHeight]);

  useEffect(() => {
    const container = divRef.current;
    function onClickElement(e: MouseEvent) {
      if (e.target && e.target instanceof HTMLAnchorElement) {
        let href = e.target.href;
        const origin = `${window.location.protocol}//${window.location.host}`;

        // si c'est un navigation locale, on bloque le behavior par défaut
        // puis on substring le host pour ne push que le pathname à react-router.
        if (href.includes(origin)) {
          e.preventDefault();
          href = href.substring(origin.length);
        }
        navigate(href);
      }
    }

    if (container) {
      container.addEventListener("click", onClickElement);
    }

    return () => {
      container?.removeEventListener("click", onClickElement);
    };
  });

  const onDoubleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (editable) {
      return;
    }
    const target = event.target;
    if (target instanceof Element && target.closest("a")) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const cell = event.currentTarget.closest(".ag-cell") ?? event.currentTarget;
    const rect = cell.getBoundingClientRect();
    openPreview(
      value ?? "",
      { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom },
      title,
    );
  };

  return (
    <div
      ref={setRef}
      col-id={colId + "-html"}
      dangerouslySetInnerHTML={{ __html: value }}
      className={tw("text-center text-sm py-1")}
      onDoubleClick={onDoubleClick}
    />
  );
}
